import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { ApiConfigService } from '../api/api-config.service';

export interface CustomerDTO {
  id: string;
  cpfcnpj: string | null;
  name: string | null;
  companyName: string | null;
  email: string | null;
  contact: string | null;
  phone: string | null;
  userRole?: string | null;
}

export interface SelectedTenant {
  id: string;
  name: string;
  cpfcnpj: string | null;
  role: string | null;
  timezone?: string | null;
}

@Injectable({ providedIn: 'root' })
export class TenantService {
  private http = inject(HttpClient);
  private api = inject(ApiConfigService);

  private _tenant = signal<SelectedTenant | null>(this.restoreFromStorage());
  readonly tenant = this._tenant.asReadonly();
  readonly activeTenantId = computed(() => this._tenant()?.id ?? null);
  readonly hasTenant = computed(() => this._tenant() !== null);

  /**
   * Timezone IANA para exibição de datas.
   * Prioridade: timezone do tenant (quando API retornar) → timezone do browser do usuário → 'America/Sao_Paulo'.
   * O campo timezone não existe nos contratos atuais da API (CompanyDTO / UserDTO não expõem o campo).
   *
   * TODO: aguardando backend
   * Frontend issue: GOWOTecnologia/gowo.hub.front#204 [BLOCKED]
   * Backend issue:  a criar quando o backend priorizar
   * Implementar: remover fallback para browserTimezone quando API retornar timezone no CompanyDTO/UserDTO
   *
   * Referência corrigida por #595 (04/09/2026) — este comentário citava #197 (épica de
   * Ajuda, fechada, sem relação nenhuma com timezone). #204 é a issue real e correta.
   */
  readonly timezone = computed(() =>
    this._tenant()?.timezone
    ?? TenantService.browserTimezone()
  );

  private static browserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
    } catch {
      return 'America/Sao_Paulo';
    }
  }

  getCustomers() {
    return this.http.get<CustomerDTO[]>(this.api.url('/api/login/customers'));
  }

  setTenant(tenant: SelectedTenant): void {
    this._tenant.set(tenant);
    localStorage.setItem('hub_tenant', JSON.stringify(tenant));
  }

  clearTenant(): void {
    this._tenant.set(null);
    localStorage.removeItem('hub_tenant');
  }

  /** Verifica se o tenantId da URL pertence ao tenant armazenado para o usuário. */
  canUseTenant(id: string): boolean {
    return this._tenant()?.id === id;
  }

  /**
   * Ativado pelo tenantGuard ao confirmar que o `:tenantId` da rota é válido.
   * Garante que o signal reflete o tenant da URL, mesmo após reload.
   */
  activateFromRoute(tenantId: string): void {
    const current = this._tenant();
    if (current?.id !== tenantId) {
      const stored = this.restoreFromStorage();
      if (stored?.id === tenantId) {
        this._tenant.set(stored);
      }
    }
  }

  /** @deprecated use activeTenantId() signal */
  getTenantId(): string | null {
    return this._tenant()?.id ?? null;
  }

  private restoreFromStorage(): SelectedTenant | null {
    try {
      const stored = localStorage.getItem('hub_tenant');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }
}
